import * as Sentry from "@sentry/react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";

import { ErrorPage } from "apps-common/components/ErrorPage";
import { PaypalCallbackPage } from "apps-common/components/PaypalCallbackPage";
import { ZuoraCallbackPage } from "apps-common/components/ZuoraCallbackPage";
import { useAuth } from "apps-common/context/auth";
import { AllowedUsers } from "apps-common/types";
import { track } from "apps-common/utils/analytics";
import { logger } from "apps-common/utils/logger";
import { ScrollToTop } from "apps-common/utils/ScrollToTop";
import { Footer } from "ui";
import { BasePage } from "ui/components/BasePage";
import { Container } from "ui/styles/containers";

import { AccountManagePage } from "./pages/AccountManagePage";
import { AddressPage } from "./pages/AddressPage";
import {
  RequestPasswordResetPage,
  ResetPasswordPage,
} from "./pages/ForgotPasswordPage";
import { LoginPage } from "./pages/LoginPage";
import { MembershipExperiencePage } from "./pages/MembershipExperiencePage";
import { MembershipHubPage } from "./pages/MembershipHubPage";
import { MembershipPlanPage } from "./pages/MembershipPlanPage";
import { PaymentMethodUpdatePage } from "./pages/PaymentMethodUpdatePage";
import { RenewSelectPlanPage } from "./pages/RenewSelectPlanPage";
import { ReviewPaymentMethodPage } from "./pages/ReviewPaymentMethod";
import { UpdatePaymentMethodSuccess } from "./pages/UpdatePaymentMethodSuccess";
import { UpdatePlanPage } from "./pages/UpdatePlanPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { ViewBillingHistoryPage } from "./pages/ViewBillingHistoryPage";
import { routes } from "./routes";
import { useStore } from "./store";
import { renewSubscription } from "./utils/renewSubscription";
import { updateAccount } from "./utils/updateAccount";
import { WithAuthAndAccessCheck } from "./utils/WithAuthAccessCheck";

// https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const Root = () => {
  const { loggedIn } = useAuth();
  return (
    <>
      <ScrollToTop />
      <Container>
        <BasePage>
          <Outlet />
          <Footer
            onLinkClick={({ name }) => {
              track({
                event: "Link Clicked",
                payload: { cta: name, location: "footer" },
              });
            }}
            showLogout={loggedIn}
          />
        </BasePage>
      </Container>
    </>
  );
};

const router = sentryCreateBrowserRouter([
  {
    path: routes.index,
    element: <Root />,
    children: [
      {
        errorElement: <ErrorPage appType="hub" />,
        children: [
          {
            index: true,
            element: (
              <WithAuthAndAccessCheck allowedUsers={[AllowedUsers.ALL]}>
                <MembershipHubPage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.login,
            element: <LoginPage />,
          },
          {
            path: routes.membershipPlanOverview,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.B2C_NO_PAYMENT_METHOD,
                  AllowedUsers.ACTIVE_B2B,
                  AllowedUsers.LIFETIME,
                ]}
              >
                <MembershipPlanPage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.reviewPaymentMethod,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2B,
                ]}
              >
                <ReviewPaymentMethodPage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.updateAddressInfo,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.B2C_NO_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2B,
                ]}
              >
                <AddressPage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.selectPlan,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.B2C_NO_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2B,
                ]}
              >
                <RenewSelectPlanPage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.updatePaymentMethod,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.B2C_NO_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2B,
                ]}
              >
                <PaymentMethodUpdatePage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.updateAccountInfo,
            element: (
              <WithAuthAndAccessCheck allowedUsers={[AllowedUsers.ALL]}>
                <AccountManagePage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.requestPasswordReset,
            element: <RequestPasswordResetPage />,
          },
          {
            path: routes.forgotPasswordResetSetPassword,
            element: <ResetPasswordPage />,
          },
          {
            path: routes.billingHistory,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                ]}
              >
                <ViewBillingHistoryPage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.verifyNewEmail,
            element: <VerifyEmailPage />,
          },
          {
            path: routes.updatePaymentMethodSuccess,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.B2C_NO_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2B,
                ]}
              >
                <UpdatePaymentMethodSuccess renew={false} />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.updatePaymentMethodSuccessRenew,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.B2C_NO_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2B,
                ]}
              >
                <UpdatePaymentMethodSuccess renew={true} />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.updatePlan,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD]}
              >
                <UpdatePlanPage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.memberExperience,
            element: (
              <WithAuthAndAccessCheck
                allowedUsers={[
                  AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD,
                  AllowedUsers.B2C_NO_PAYMENT_METHOD,
                  AllowedUsers.PENDING_B2C,
                  AllowedUsers.ACTIVE_B2B,
                  AllowedUsers.INACTIVE_B2B,
                  AllowedUsers.LIFETIME,
                ]}
              >
                <MembershipExperiencePage />
              </WithAuthAndAccessCheck>
            ),
          },
          {
            path: routes.paypalCallback,
            element: (
              <PaypalCallbackPage
                onSuccess={async ({ paymentMethodId, flow }) => {
                  const email = useStore.getState().email;
                  const { paypalGatewayName: gatewayName } =
                    useStore.getState().gateways;
                  const ratePlan = useStore.getState().selectedRatePlan!;
                  const userAddressForm = useStore.getState().userAddressForm;
                  const { currency, billingPeriod } = ratePlan;

                  try {
                    userAddressForm &&
                      (await updateAccount(
                        paymentMethodId,
                        gatewayName,
                        userAddressForm,
                        email,
                        currency,
                      ));

                    logger.info("Paypal callback update account success", {
                      flow,
                    });

                    if (flow === "renew") {
                      await renewSubscription(billingPeriod);
                      logger.info(
                        "Paypal callback renew subscription success",
                        {
                          flow,
                        },
                      );
                      window.parent.location.replace(
                        routes.updatePaymentMethodSuccessRenew,
                      );
                    } else {
                      window.parent.location.replace(
                        routes.updatePaymentMethodSuccess,
                      );
                    }
                  } catch (error) {
                    logger.error("Error in Paypal callback", {
                      error,
                    });
                    window.parent.location.replace(routes.error);
                  }
                }}
                onError={(error) => {
                  logger.error("Error in Paypal callback", {
                    error,
                  });
                  window.sessionStorage.setItem("paypalError", "true");
                  window.location.replace(routes.updatePaymentMethod);
                }}
                isUnitedStates={
                  useStore.getState().userAddressForm?.shippingAddress
                    ?.country === "US"
                }
                paymentGateway={useStore.getState().gateways.paypalGatewayName}
              />
            ),
          },
          {
            path: routes.error,
            element: <ErrorPage errorPageType={"error"} appType="hub" />,
          },
          {
            path: "*",
            element: <ErrorPage errorPageType={"notFound"} appType="hub" />,
          },
        ],
      },
    ],
  },
  {
    path: routes.paymentMethodCallback, // Don't add as a children of the main route, as it's rendered inside an iframe
    element: (
      <ZuoraCallbackPage
        onSuccess={async ({ paymentMethodId, flow }) => {
          const email = useStore.getState().email;
          const { creditCardGatewayName: gatewayName } =
            useStore.getState().gateways;
          const ratePlan = useStore.getState().selectedRatePlan!;
          const userAddressForm = useStore.getState().userAddressForm;
          const { currency, billingPeriod } = ratePlan;

          try {
            userAddressForm &&
              (await updateAccount(
                paymentMethodId,
                gatewayName,
                userAddressForm,
                email,
                currency,
              ));

            logger.info("Zuora callback update account success", { flow });

            if (flow === "renew") {
              await renewSubscription(billingPeriod);
              logger.info("Zuora callback renew subscription success", {
                flow,
              });
              window.parent.location.replace(
                routes.updatePaymentMethodSuccessRenew,
              );
            } else {
              window.parent.location.replace(routes.updatePaymentMethodSuccess);
            }
          } catch (e) {
            window.parent.location.replace(routes.error);
          }
        }}
        onError={(error) => {
          logger.error("Error in Zuora callback", { error });
          const { code, message } = error;
          window.sessionStorage.setItem(
            "zuoraResponse",
            JSON.stringify({ message, code }),
          );
          window.parent.location.replace(routes.updatePaymentMethod);
        }}
      />
    ),
  },
]);

export const Router = () => <RouterProvider router={router} />;
